import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { BaseApiService } from '../../../core/api/base-api.service';
import type { BulkCancelSalesRequest } from '../../../core/api/generated/models/BulkCancelSalesRequest';
import type { RevenueBulkResult } from '../../../core/api/generated/models/RevenueBulkResult';
import type { RevenueSaleDetail } from '../../../core/api/generated/models/RevenueSaleDetail';
import type { CreateSalePayload, SaleListItem, SaleListResponse } from './sales.types';

export type SalesSearchField =
  | 'contact_name'
  | 'contact_email'
  | 'contact_phone'
  | 'product_name'
  | 'external_id';

export type SaleSortBy = 'created_at' | 'total' | 'status' | 'type';

export interface SaleQuery {
  status?: string | string[];
  type?: 'one_time' | 'recurring';
  contact_id?: string;
  product_id?: string;
  search?: string;
  search_field?: SalesSearchField;
  page?: number;
  pageSize?: number;
  sort_by?: SaleSortBy;
  sort_order?: 'asc' | 'desc';
}

@Injectable({ providedIn: 'root' })
export class SalesApiService extends BaseApiService {
  protected override readonly resourcePath = 'v1/revenue';

  listSales(query: SaleQuery = {}) {
    return this.get<SaleListResponse>('sales', { params: { pageSize: 50, ...query } });
  }

  getSale(id: string) {
    return this.get<RevenueSaleDetail>(['sales', id]);
  }

  createSale(body: CreateSalePayload) {
    return this.post<RevenueSaleDetail, CreateSalePayload>('sales', body);
  }

  updateSale(id: string, body: { quantity?: number; valid_until?: string | null }) {
    return this.patch<RevenueSaleDetail, typeof body>(['sales', id], body);
  }

  activateSale(id: string, reason?: string) {
    return this.post<SaleListItem, { reason?: string }>(['sales', id, 'activate'], { reason });
  }

  cancelSale(id: string, reason?: string) {
    return this.post<SaleListItem, { reason?: string }>(['sales', id, 'cancel'], { reason });
  }

  deleteSale(id: string) {
    return this.delete<void>(['sales', id]);
  }

  refundSale(id: string, refundAmount?: string, reason?: string) {
    return this.post<SaleListItem, { refund_amount?: string; reason?: string }>(
      ['sales', id, 'refund'],
      { refund_amount: refundAmount, reason },
    );
  }

  registerCyclePayment(id: string) {
    return this.post<SaleListItem, Record<string, never>>(
      ['sales', id, 'register-cycle-payment'],
      {},
    );
  }

  // ── Bulk + export ──
  bulkCancelSales(body: BulkCancelSalesRequest) {
    return this.post<RevenueBulkResult, BulkCancelSalesRequest>('sales/bulk-cancel', body);
  }

  exportSales(params: Record<string, string | undefined>): Observable<Blob> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') httpParams = httpParams.set(key, value);
    }
    return this.http.get(`${this.baseUrl}/${this.resourcePath}/sales/export`, {
      params: httpParams,
      headers: new HttpHeaders({ ...this.apiContext.headers() }),
      withCredentials: true,
      responseType: 'blob',
    });
  }
}
